import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import RootStore from 'stores/Root/Root';
import API from 'utils/api';
import { EventType } from 'utils/events/constants';

import Logger from 'utils/logger';

import { FlattenedTreeItem, FolderTreeItemKind, FolderType } from './types';

interface DeleteFolderErrorResponse {
  error: string;
  documentsCount?: number;
}

interface CreateFolderErrorResponse {
  parent?: string[];
  name?: string[];
}

export default class FoldersStore {
  store: RootStore;

  api: typeof API;

  @observable isLoading = false;

  @observable isLoaded = false;

  @observable folders: FolderType[] = [];

  @observable selectedFolderId: string | null = null;

  @observable selectedDocumentId: string | null = null;

  @observable expandedFolderIds: Set<string> = new Set();

  @observable hasError = false;

  constructor(rootStore: RootStore, api: typeof API) {
    this.store = rootStore;
    this.api = api;

    makeObservable(this);
    eventBus.register(this);
  }

  private traverseTree<T>(folders: FolderType[], visitor: (folder: FolderType, depth: number) => T): T[] {
    const result: T[] = [];
    const traverse = (folderList: FolderType[], depth: number): void => {
      folderList.forEach((folder) => {
        result.push(visitor(folder, depth));
        if (folder.subfolders.length > 0) {
          traverse(folder.subfolders, depth + 1);
        }
      });
    };
    traverse(folders, 0);
    return result;
  }

  private findFolderPath<T>(targetId: string, folders: FolderType[], mapFn: (folder: FolderType) => T): T[] | null {
    const search = (folderList: FolderType[], path: T[]): T[] | null => {
      for (const folder of folderList) {
        const currentPath = [...path, mapFn(folder)];
        if (folder.uuid === targetId) {
          return currentPath;
        }
        if (folder.subfolders.length > 0) {
          const found = search(folder.subfolders, currentPath);
          if (found) return found;
        }
      }
      return null;
    };
    return search(folders, []);
  }

  @computed
  get flatFolders(): FolderType[] {
    return this.traverseTree(this.folders, (folder) => folder);
  }

  // Mirrors the rows the sidebar renders, so dnd-kit's sortable set lines up with the DOM.
  @computed
  get flattenedTree(): FlattenedTreeItem[] {
    const rows: FlattenedTreeItem[] = [];

    const walk = (folderList: FolderType[], parentUuid: string | null, depth: number): void => {
      folderList.forEach((folder) => {
        rows.push({ uuid: folder.uuid, kind: FolderTreeItemKind.Folder, parentUuid, depth });

        if (!this.expandedFolderIds.has(folder.uuid)) return;

        walk(folder.subfolders ?? [], folder.uuid, depth + 1);
        (folder.documents ?? []).forEach((document) => {
          rows.push({
            uuid: document.uuid,
            kind: FolderTreeItemKind.Document,
            parentUuid: folder.uuid,
            depth: depth + 1,
          });
        });
      });
    };

    walk(this.folders, null, 0);
    return rows;
  }

  @computed
  get reorderableUuids(): Set<string> {
    const groupKey = (row: FlattenedTreeItem): string => `${row.parentUuid}:${row.kind}`;
    const sizes = new Map<string, number>();
    this.flattenedTree.forEach((row) => {
      sizes.set(groupKey(row), (sizes.get(groupKey(row)) ?? 0) + 1);
    });

    return new Set(this.flattenedTree.filter((row) => (sizes.get(groupKey(row)) ?? 0) > 1).map((row) => row.uuid));
  }

  treeRow(uuid: string): FlattenedTreeItem | undefined {
    return this.flattenedTree.find((row) => row.uuid === uuid);
  }

  siblingsOf(row: FlattenedTreeItem): FlattenedTreeItem[] {
    return this.flattenedTree.filter(
      (candidate) => candidate.kind === row.kind && candidate.parentUuid === row.parentUuid
    );
  }

  areSiblings(first: FlattenedTreeItem, second: FlattenedTreeItem): boolean {
    return first.kind === second.kind && first.parentUuid === second.parentUuid;
  }

  moveWithinSiblings(dragged: FlattenedTreeItem, target: FlattenedTreeItem): void {
    const group = this.siblingsOf(dragged).map((row) => row.uuid);
    const from = group.indexOf(dragged.uuid);
    const to = group.indexOf(target.uuid);
    if (from === -1 || to === -1 || from === to) return;

    const reordered = [...group];
    reordered.splice(to, 0, ...reordered.splice(from, 1));
    this.reorderSiblings(dragged.parentUuid, dragged.kind, reordered);
  }

  getFolderPath(targetId: string): string {
    const pathArray = this.findFolderPath(targetId, this.folders, (folder) => folder.name);
    return pathArray ? pathArray.join('/') : '';
  }

  getFolderAncestorsByDocumentUuid(documentUuid: string): { uuid: string; name: string }[] {
    const findFolderContainingDocument = (
      folderList: FolderType[],
      path: { uuid: string; name: string }[]
    ): { uuid: string; name: string }[] | null => {
      for (const folder of folderList) {
        const currentPath = [...path, { uuid: folder.uuid, name: folder.name }];
        if (folder.documents?.some((doc) => doc.uuid === documentUuid)) {
          return currentPath;
        }
        if (folder.subfolders?.length > 0) {
          const found = findFolderContainingDocument(folder.subfolders, currentPath);
          if (found) return found;
        }
      }
      return null;
    };
    return findFolderContainingDocument(this.folders, []) || [];
  }

  @action.bound
  async loadFolders(): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.hasError = false;

    try {
      const { data } = await this.api.entities.loadFolders()();
      runInAction(() => {
        this.folders = data.folders;
        this.isLoaded = true;
      });
    } catch (error) {
      runInAction(() => {
        this.hasError = true;
      });
      Logger.error('Failed to load folders', error);
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  @action.bound
  toggleExpanded(folderId: string): void {
    const newSet = new Set(this.expandedFolderIds);
    newSet.has(folderId) ? newSet.delete(folderId) : newSet.add(folderId);
    this.expandedFolderIds = newSet;
  }

  @action.bound
  expandToDocument(documentUuid: string): void {
    this.selectedDocumentId = documentUuid;
    this.selectedFolderId = null;
    const ancestors = this.getFolderAncestorsByDocumentUuid(documentUuid);
    if (ancestors.length > 0) {
      const newSet = new Set(this.expandedFolderIds);
      ancestors.forEach((folder) => newSet.add(folder.uuid));
      this.expandedFolderIds = newSet;
    }
  }

  @action.bound
  clearSelectedDocument(): void {
    this.selectedDocumentId = null;
  }

  @action.bound
  selectFolder(folderId: string | null): void {
    this.selectedFolderId = folderId;
    this.selectedDocumentId = null;

    if (folderId) {
      const ancestors = this.findFolderPath(folderId, this.folders, (folder) => folder.uuid);
      if (ancestors && ancestors.length > 0) {
        const newSet = new Set(this.expandedFolderIds);
        ancestors.forEach((uuid) => newSet.add(uuid));
        this.expandedFolderIds = newSet;
      }
    }
  }

  @action.bound
  async createFolder(name: string, parent: string | null): Promise<void> {
    try {
      await this.api.entities.createFolder(name, parent)();
      await this.loadFolders();
      this.store.notification.enqueueSuccessSnackbar('Folder created successfully');
    } catch (e) {
      const error = e as AxiosError<CreateFolderErrorResponse>;
      const errorData = error.response?.data;
      const errorMessage = errorData?.parent?.[0] || errorData?.name?.[0] || 'Failed to create folder';
      Logger.error('Failed to create folder', error);
      this.store.notification.enqueueErrorSnackbar(errorMessage);
    }
  }

  @action.bound
  async editFolder(folderId: string, name: string, parent: string | null): Promise<void> {
    try {
      await this.api.entities.editFolder(folderId, name, parent)();
      await this.loadFolders();
      this.store.notification.enqueueSuccessSnackbar('Folder edited successfully');
    } catch (error) {
      Logger.error('Failed to edit folder', error);
      this.store.notification.enqueueErrorSnackbar('Failed to edit folder');
    }
  }

  @action.bound
  async deleteFolder(folderId: string): Promise<void> {
    try {
      await this.api.entities.deleteFolder(folderId)();
      await this.loadFolders();
      this.store.notification.enqueueSuccessSnackbar('Folder deleted successfully');
    } catch (e) {
      const error = e as AxiosError<DeleteFolderErrorResponse>;
      const errorMessage = error.response?.data?.error || 'Failed to delete folder';
      Logger.error('Failed to delete folder', error);
      this.store.notification.enqueueErrorSnackbar(errorMessage);
    }
  }

  private mapFolderTree(
    folders: FolderType[],
    folderUuid: string,
    updater: (folder: FolderType) => FolderType
  ): FolderType[] {
    return folders.map((folder) => {
      if (folder.uuid === folderUuid) {
        return updater(folder);
      }
      if (folder.subfolders?.length > 0) {
        return { ...folder, subfolders: this.mapFolderTree(folder.subfolders, folderUuid, updater) };
      }
      return folder;
    });
  }

  private applyOrder<T extends { uuid: string }>(items: T[], orderedUuids: string[]): T[] {
    const byUuid = new Map(items.map((item) => [item.uuid, item]));
    return orderedUuids.map((uuid) => byUuid.get(uuid)).filter((item): item is T => item !== undefined);
  }

  @action.bound
  async reorderSiblings(parent: string | null, kind: FolderTreeItemKind, orderedUuids: string[]): Promise<void> {
    const snapshot = this.folders;

    if (parent === null) {
      this.folders = this.applyOrder(this.folders, orderedUuids);
    } else {
      this.folders = this.mapFolderTree(this.folders, parent, (folder) =>
        kind === FolderTreeItemKind.Folder
          ? { ...folder, subfolders: this.applyOrder(folder.subfolders, orderedUuids) }
          : { ...folder, documents: this.applyOrder(folder.documents, orderedUuids) }
      );
    }

    try {
      await this.api.entities.reorderFolderContents(parent, { [kind]: orderedUuids })();
    } catch (error) {
      runInAction(() => {
        this.folders = snapshot;
      });
      Logger.error('Failed to reorder folder contents', error);
      this.store.notification.enqueueErrorSnackbar('Failed to save the new order');
      await this.loadFolders();
    }
  }

  @action.bound
  @subscribe(EventType.LoggedOut)
  reset(): void {
    this.folders = [];
    this.selectedFolderId = null;
    this.selectedDocumentId = null;
    this.expandedFolderIds = new Set();
    this.isLoaded = false;
    this.hasError = false;
  }
}
